import axios from 'axios';

import {
  type FunctionComponent,
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

import {
  Button,
  Checkbox,
  Input,
  Link,
} from '@dr.pogodin/react-utils';

import { captureException } from '@sentry/react';

import Throbber from 'components/Throbber';
import { MediaFileUpload } from 'components/admin';
import MessageModal from 'components/admin/MessageModal';
import AdminVideoUpload from 'components/admin/VideoUpload';

import { useUserAssets } from 'hooks';
import useUserArtist from 'hooks/useUserArtist';

import {
  updateArtist,
  uploadDocument,
  uploadIntroVideo,
  uploadVideo,
} from 'services/artists';

import {
  type AssetT,
  E_SUBMIT,
  P_API_ARTIST,
  getConfig,
  getId,
  useGlobalState,
} from 'utils';

import IconCircledMinus from 'assets/images/icon-circled-minus-24px.svg';
import IconCircledPlus from 'assets/images/icon-circled-plus-24px.svg';
import IconDesktop from 'assets/images/icon-desktop-20px.svg';

import IconInfo from 'assets/images/icon-info-24px.svg';
import IconLinkedIn from 'assets/images/icon-linkedin-20px.svg';
import IconTiktok from 'assets/images/icon-tiktok-20px.svg';
import IconFacebook from 'assets/images/logo-facebook-gray-20px.svg';

import IconInstagram from 'assets/images/logo-instagram-gray-20px.svg';

import IconX from 'assets/images/logo-x-gray-20px.svg';

import IconYouTube from 'assets/images/logo-youtube-gray-20px.svg';

import secondaryButtonTheme from 'themes/Button/admin/secondary-v2.scss';

import type { TabRefT } from '../common';

import TocModal from './TocModal';

import inputThemeB from './input-b.scss';

import S from './style.scss';

import videoUploadTheme from './video-upload.scss';

const ERROR_TOC_REQUIRED = 'Terms and conditions must be agreed';
const ERROR_VIDEO_REQUIRED = 'Intro video is required';

type PropsT = {
  goToPrev: () => void;
  ref: Ref<TabRefT>;
};

// eslint-disable-next-line complexity
const Tab04: FunctionComponent<PropsT> = ({ goToPrev, ref }) => {
  const [userId] = useGlobalState('user.id');
  if (!userId) throw Error('Not authenticated');

  const practitioner = useUserArtist();

  const pd = practitioner.data;

  const [facebook, setFacebook] = useState<string>(pd?.facebook ?? '');
  const [instagram, setInstagram] = useState<string>(pd?.instagram ?? '');
  const [linkedIn, setLinkedIn] = useState<string>(pd?.linkedIn ?? '');

  const [
    otherLinks,
    setOtherLinks,
  ] = useState<string[]>(pd?.otherLinks?.length ? pd.otherLinks : ['']);

  const [tiktok, setTiktok] = useState<string>(pd?.tikTok ?? '');
  const [xTwitter, setXTwitter] = useState<string>(pd?.xTwitter ?? '');
  const [youtube, setYoutube] = useState<string>(pd?.youtube ?? '');

  const [localFiles, setLocalFiles] = useState<File[]>([]);

  const assets = useUserAssets(userId);

  const [
    assetsToRemove,
    setAssetsToRemove,
  ] = useState<Record<string, true>>({});

  const filterVisibleAssets
    = (items: AssetT[]) => items.filter((it) => !assetsToRemove[it.id]);

  const [filesToRemove, setFilesToRemove] = useState<Record<string, true>>({});

  const [video, setVideo] = useState<File | string>();
  const [videoError, setVideoError] = useState<string>();

  const videoValid = !!(video ?? practitioner.data?.videoUrl);

  const [showToC, setShowToC] = useState(false);
  const [tocError, setTocError] = useState<string>();

  const [tocAccepted, setTocAccepted] = useState<boolean>(
    pd?.termsAndConditionsAccepted ?? false,
  );

  useEffect(() => {
    // TODO: Refactor.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFacebook(pd?.facebook ?? '');
    setInstagram(pd?.instagram ?? '');
    setLinkedIn(pd?.linkedIn ?? '');
    setOtherLinks(pd?.otherLinks?.length ? pd.otherLinks : ['']);
    setTiktok(pd?.tikTok ?? '');
    setTocAccepted(pd?.termsAndConditionsAccepted ?? false);
    setXTwitter(pd?.xTwitter ?? '');
    setYoutube(pd?.youtube ?? '');
  }, [
    pd?.facebook,
    pd?.instagram,
    pd?.linkedIn,
    pd?.otherLinks,
    pd?.termsAndConditionsAccepted,
    pd?.tikTok,
    pd?.xTwitter,
    pd?.youtube,
  ]);

  const filterFiles = useCallback((
    urls: string[],
  ) => urls.filter((item) => !filesToRemove[item]), [filesToRemove]);

  const [busy, setBusy] = useState<boolean | number>(false);

  const [showDone, setShowDone] = useState(false);

  // TODO: This needs refactoring & testing when React Compiler is enabled.
  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const save = useCallback(async () => {
    try {
      setBusy(0);
      let totalSize = 0;
      let uploadedBeforeSize = 0;
      let uploadedSize = 0;
      for (const file of localFiles) totalSize += file.size;

      const updateProgress = (progress: number, size: number) => {
        uploadedSize = uploadedBeforeSize + progress * size;
        setBusy(Math.round(100 * uploadedSize / totalSize));
      };

      for (const assetId of Object.keys(assetsToRemove)) {
        await assets.deleteAsset(assetId);
      }
      setAssetsToRemove({});

      // WE NEED TO HANDLE ASSETS SEPARATELY!

      // TODO: Remove media files.

      await updateArtist(getId(practitioner.data), {
        facebook,
        instagram,
        linkedIn,
        otherLinks,
        termsAndConditionsAccepted: tocAccepted,
        tikTok: tiktok,
        videos: filterFiles(pd?.videos ?? []),
        xTwitter,
        youtube,
      });

      setFilesToRemove({});

      if (video) {
        if (!(video instanceof File)) throw Error('Not implemented yet');

        await uploadIntroVideo({
          file: video,
          onProgress: (value) => {
            setBusy(Math.round(100 * value));
          },
          practitionerId: getId(practitioner.data),
        });

        setVideo(undefined);
      }

      for (const file of localFiles) {
        const practitionerId = getId(practitioner.data);
        if (file.type.startsWith('video/')) {
          await uploadVideo({
            file,
            onProgress: (value) => {
              updateProgress(value, file.size);
            },
            practitionerId,
          });
        } else {
          await uploadDocument({
            document: file,
            practitionerId,
          });
        }
        uploadedBeforeSize += file.size;
      }

      setLocalFiles([]);

      await assets.reload();
      await practitioner.reload();
    } catch (error) {
      captureException(error);
      // eslint-disable-next-line no-alert
      alert('Failed to save updates');
    }

    setBusy(false);
  }, [
    assets,
    assetsToRemove,
    facebook,
    localFiles,
    filterFiles,
    instagram,
    linkedIn,
    otherLinks,
    pd?.videos,
    practitioner,
    tiktok,
    tocAccepted,
    video,
    xTwitter,
    youtube,
  ]);

  const submit = async () => {
    try {
      setBusy(true);

      const config = getConfig(true);

      await axios.post(
        `${P_API_ARTIST}/${getId(practitioner.data)}${E_SUBMIT}`,
        { _csrf: config.CSRF },
      );

      setShowDone(true);
    } catch (error) {
      captureException(error);
      // eslint-disable-next-line no-alert
      alert('Failed to submit the application');
    }

    setBusy(false);
  };

  useImperativeHandle(ref, () => ({
    save,
    valid: true,
  }), [save]);

  const otherLinkNodes: ReactNode[] = [];
  for (let i = 0; i < otherLinks.length; ++i) {
    const link = otherLinks[i];
    otherLinkNodes.push(
      <div key={`other-link/${i}`} styleName="iconAndInput">
        <IconDesktop />
        <Input
          label="Other relevant links"
          onChange={(e) => {
            const links = [...otherLinks];
            links[i] = e.target.value;
            setOtherLinks(links);
          }}
          theme={inputThemeB}
          value={link}
        />
        {i === otherLinks.length - 1 ? (
          <Link
            keepScrollPosition
            onClick={() => {
              setOtherLinks([...otherLinks, '']);
            }}
            styleName="flex"
            to=""
          >
            <IconCircledPlus />
          </Link>
        ) : (
          <Link
            keepScrollPosition
            onClick={() => {
              const links = [...otherLinks];
              links.splice(i, 1);
              setOtherLinks(links);
            }}
            styleName="flex"
            to=""
          >
            <IconCircledMinus />
          </Link>
        )}
      </div>,
    );
  }

  let uploadIntoVideoLabelClassname = S.uploadIntoVideoLabel;
  if (videoError) uploadIntoVideoLabelClassname += ` ${S.redColor}`;

  return (
    <div styleName="container">
      <p styleName="topText">
        Help our team experience your teaching approach. Share 2-3 content
        pieces that showcase how you guide others. Upload files or share links
        to your existing work.
      </p>
      <h2>Share Your Practice</h2>
      <p>Drag and drop your media files in the following box</p>
      <MediaFileUpload
        assets={filterVisibleAssets(assets.data ?? [])}
        files={filterFiles(pd?.videos ?? [])}
        localFiles={localFiles}
        removeAsset={(asset: AssetT) => {
          setAssetsToRemove({
            ...assetsToRemove,
            [asset.id]: true,
          });
        }}
        removeFile={(url: string) => {
          setFilesToRemove({
            ...filesToRemove,
            [url]: true,
          });
        }}
        setLocalFiles={setLocalFiles}
      />
      <h2>Professional Links</h2>
      <div styleName="socialLinksBlock">
        <div styleName="iconAndInput">
          <IconLinkedIn />
          <Input
            label="LinkedIn profile"
            onChange={(e) => {
              let { value } = e.target;
              if (value && !value.startsWith('@')) value = `@${value}`;
              setLinkedIn(value);
            }}
            placeholder="@handle"
            theme={inputThemeB}
            value={linkedIn}
          />
        </div>
        <div styleName="iconAndInput">
          <IconInstagram />
          <Input
            label="Instagram profile"
            onChange={(e) => {
              let { value } = e.target;
              if (value && !value.startsWith('@')) value = `@${value}`;
              setInstagram(value);
            }}
            placeholder="@handle"
            theme={inputThemeB}
            value={instagram}
          />
        </div>
        <div styleName="iconAndInput">
          <IconFacebook />
          <Input
            label="Facebook profile"
            onChange={(e) => {
              let { value } = e.target;
              if (value && !value.startsWith('@')) value = `@${value}`;
              setFacebook(value);
            }}
            placeholder="@handle"
            theme={inputThemeB}
            value={facebook}
          />
        </div>

        <div styleName="iconAndInput">
          <IconTiktok />
          <Input
            label="TikTok profile"
            onChange={(e) => {
              let { value } = e.target;
              if (value && !value.startsWith('@')) value = `@${value}`;
              setTiktok(value);
            }}
            placeholder="@handle"
            theme={inputThemeB}
            value={tiktok}
          />
        </div>
        <div styleName="iconAndInput">
          <IconX />
          <Input
            label="X profile"
            onChange={(e) => {
              let { value } = e.target;
              if (value && !value.startsWith('@')) value = `@${value}`;
              setXTwitter(value);
            }}
            placeholder="@handle"
            theme={inputThemeB}
            value={xTwitter}
          />
        </div>
        <div styleName="iconAndInput">
          <IconYouTube />
          <Input
            label="Youtube Channel"
            onChange={(e) => {
              let { value } = e.target;
              if (value && !value.startsWith('@')) value = `@${value}`;
              setYoutube(value);
            }}
            placeholder="@handle"
            theme={inputThemeB}
            value={youtube}
          />
        </div>
        {otherLinkNodes}
      </div>
      <div>
        <div styleName="row-space-between">
          <div className={uploadIntoVideoLabelClassname}>
            Upload an intro video <IconInfo />
          </div>
          <div styleName="requiredField">
            Required field
          </div>
        </div>
        <AdminVideoUpload
          error={videoError}
          localVideo={video}
          setLocalVideo={(newVideo) => {
            setVideoError(newVideo ? undefined : ERROR_VIDEO_REQUIRED);
            setVideo(newVideo);
          }}
          theme={videoUploadTheme}
          video={practitioner.data?.videoUrl ? {
            isReadyToBePlayed: !!practitioner.data.videoIsReadyToBePlayed,
            url: practitioner.data.videoUrl,
          } : null}
        />
      </div>
      <h2>Terms and Conditions</h2>
      <Checkbox
        checked={tocAccepted}
        error={tocError}
        label={(
          <p>
            I agree to the &zwnj;
            <Link
              keepScrollPosition
              onClick={() => {
                setShowToC(true);
              }}
              to=""
            >
              Terms and Conditions
            </Link>
            &zwnj; and confirm my professional eligibility.
          </p>
        )}
        onChange={() => {
          const newState = !tocAccepted;
          setTocAccepted(newState);
          setTocError(newState ? undefined : ERROR_TOC_REQUIRED);
        }}
      />
      <div styleName="hr" />
      <div styleName="buttons">
        <Button
          onClick={() => {
            void (async () => {
              await save();
              goToPrev();
            })();
          }}
          theme={secondaryButtonTheme}
        >
          Previous
        </Button>
        <Button
          // disabled={!tocAccepted || !videoValid}
          onClick={() => {
            if (tocAccepted && videoValid) {
              void (async () => {
                await save();
                await submit();
              })();
            } else {
              if (!videoValid) setVideoError(ERROR_VIDEO_REQUIRED);
              if (!tocAccepted) setTocError(ERROR_TOC_REQUIRED);
            }
          }}
        >
          Send Application
        </Button>
      </div>
      {showToC ? (
        <TocModal
          onClose={() => {
            setShowToC(false);
          }}
        />
      ) : null}
      {busy === false ? null : (
        <Throbber
          progress={typeof busy === 'number' ? busy : undefined}
          withOverlay
        />
      )}
      {showDone ? (
        <MessageModal
          cancelButtonLabel="Understood"
          icon="OK"
          onCancel={() => {
            setShowDone(false);
          }}
        >
          Your application has been successfully submitted. Until it is reviewed
          you may further edit your application, pressing &laquo;Submit&raquo;
          button again to save your updates.
        </MessageModal>
      ) : null}
    </div>
  );
};

export default Tab04;
